import { distinctUntilChanged } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type AbstractControl, ControlContainer, FormGroupDirective } from '@angular/forms';
import type { DropdownChangeEvent } from 'primeng/dropdown';
import * as FlatModels from '@medsurf/flat-models';

@Component({
    selector: 'medsurf-design-form-control-input-color-picker',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './color-picker.input.form-control.design.html',
    styleUrls: ['./color-picker.input.form-control.design.scss'],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ],
    standalone: false
})
export class ColorPickerInputFormControlDesign implements OnInit, OnChanges {
  @Input() formField = true;
  @Input({ required: true }) control!: AbstractControl;
  @Input() inputClass: FlatModels.TextDesignModels.TextDesignClass = FlatModels.TextDesignModels.TextDesignClass.DEFAULT;
  @Input() menuClass: FlatModels.PanelDesignModels.MenuDesignClass = FlatModels.PanelDesignModels.MenuDesignClass.DEFAULT;
  @Input() label?: string;
  @Input() disabled = false;
  @Input() withEyeDropper = false;
  @Output() valueChange = new EventEmitter<DropdownChangeEvent>();
  
  readonly FlatModels = FlatModels;

  name?: string;

  constructor(private cd: ChangeDetectorRef, private destroyRef: DestroyRef) {
  }

  ngOnInit() {
    const formGroup = this.control.parent;
    this.name = formGroup ? Object.keys(formGroup.controls).find(name => this.control === formGroup.get(name)) : undefined;

    this.control?.statusChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe(() => {
      this.cd.markForCheck();
    });

    if (this.name === undefined) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.form_control_name_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatDesign.ColorPickerInputFormControlDesign.ngOnInit"
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']?.currentValue) {
      this.control?.disable();
    } else {
      this.control?.enable();
    }
  }
  
  supportsEyeDropper() {
    return ('EyeDropper' in window);
  }
  
  async startEyeDropper() {
    const eyeDropper = new (window as any).EyeDropper();
    const color = await eyeDropper.open();
    this.onColorChange(color.sRGBHex);
  }
  
  resetColor() {
    this.control.patchValue(null);
    this.valueChange.emit({
      originalEvent: new MouseEvent('force'),
      value: null
    });
  }

  onColorChange(event: string) {
    this.control.patchValue(event);
    this.valueChange.emit({
      originalEvent: new MouseEvent('force'),
      value: event
    });
  }
}
